import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { OBJECTIVE_PERIODS } from '../utils';
import { Widget } from './Widget';

const typeLabel = objective => i18next.t(`common.${objective.get('analysis_type')}`).capitalize();

const objectiveSiteId = objective => objective?.indicator()?.get('site_id') || '';

export function NewObjectiveData({ data, onChangeField, onCompleted }) {
    const
        initialObjective = WiseMetering.objectives.get(data.data?.objectiveId),
        [site, setSite] = useState(objectiveSiteId(initialObjective));

    const
        decimalOptions = ['0', '1', '2', '3'],
        unitScaleOptions = [
            { label: i18next.t('unit_scales.auto'), value: -1 },
            { label: i18next.t('unit_scales.full'), value: 0 },
            { label: i18next.t('unit_scales.thousands'), value: 3 },
            { label: i18next.t('unit_scales.millions'), value: 6 },
            { label: i18next.t('unit_scales.billions'), value: 9 }
        ],
        sitesOptions = WiseMetering.zones.activeSites().map(model => ({ id: model.id, name: model.get('name') })),
        objectiveOptions = WiseMetering.objectives
            .filter(objective => objectiveSiteId(objective) === site)
            .sort((a, b) => b.get('year') - a.get('year'))
            .map(objective => ({
                id: objective.id,
                name: `${objective.indicator().get('name')} · ${typeLabel(objective)} · ${objective.get('year')}`,
                objective
            }));

    // Radix hands back strings, so the numeric field is coerced rather than
    // declared as z.number() — which would reject '3' and silently block Next.
    const formSchema = z.object({
        decimals: z.string().min(1, i18next.t('form_validations.generic.required')),
        objectiveId: z.string().min(1, i18next.t('form_validations.generic.required')),
        period: z.enum(OBJECTIVE_PERIODS),
        site: z.string().min(1, i18next.t('form_validations.generic.required')),
        unitScale: z.coerce.number().int(i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            decimals: data.data?.decimals || '2',
            objectiveId: data.data?.objectiveId || '',
            period: data.data?.period || OBJECTIVE_PERIODS[0],
            site,
            unitScale: data.data?.unitScale ?? -1
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        decimals: debounce(() => form.trigger('decimals'), 600),
        objectiveId: debounce(() => form.trigger('objectiveId'), 600),
        period: debounce(() => form.trigger('period'), 600),
        unitScale: debounce(() => form.trigger('unitScale'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <FormField
                        control={form.control}
                        name="site"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.site').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);
                                            setSite(val);

                                            const selected = sitesOptions.find(i => i.id === val);

                                            onChangeField('subtitle')(selected?.name || '');

                                            form.setValue('objectiveId', '');
                                            onChangeField('data')({
                                                ...data.data,
                                                objectiveId: ''
                                            });

                                            debouncedTriggers.current.objectiveId();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('sites.select_site')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sitesOptions.map(site => (
                                                <SelectItem key={site.id} value={site.id}>
                                                    {site.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="objectiveId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.objective').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        disabled={!site || objectiveOptions.length === 0}
                                        onValueChange={val => {
                                            field.onChange(val);

                                            const { objective } = objectiveOptions.find(i => i.id === val) || {};

                                            onChangeField('data')({
                                                ...data.data,
                                                objectiveId: val
                                            });

                                            onChangeField('title')(
                                                objective ? `${objective.indicator().get('name')} - ${typeLabel(objective)}` : ''
                                            );

                                            debouncedTriggers.current.objectiveId();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue
                                                placeholder={site && objectiveOptions.length === 0
                                                    ? i18next.t('dashboard.custom_dashboard.no_objectives_for_site')
                                                    : i18next.t('dashboard.custom_dashboard.select_an_objective')}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {objectiveOptions.map(option => (
                                                <SelectItem key={option.id} value={option.id}>
                                                    {option.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="period"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.reference_period').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                period: val
                                            });

                                            debouncedTriggers.current.period();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('dashboard.custom_dashboard.select_a_period')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {OBJECTIVE_PERIODS.map(period => (
                                                <SelectItem key={period} value={period}>
                                                    {i18next.t(`periods.verbose.${period}`).capitalize()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="unitScale"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('unit_scales.auto').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(Number(val));

                                            onChangeField('data')({
                                                ...data.data,
                                                unitScale: Number(val)
                                            });

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={String(field.value)}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('unit_scales.auto')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {unitScaleOptions.map(unitScale => (
                                                <SelectItem key={unitScale.value} value={String(unitScale.value)}>
                                                    {unitScale.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="decimals"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.decimals').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                decimals: val
                                            });

                                            debouncedTriggers.current.decimals();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('glossary.decimals').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {decimalOptions.map(decimal => (
                                                <SelectItem key={decimal} value={decimal}>
                                                    {decimal}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
